<template>
  <div :class="isMobile ? '' : c" ref="mHeaderContainer" v-resize="measureIsMobile">
    <v-breadcrumbs v-if="breadCrumbs" class="ml-n2 pt-2 pb-0" :items="breadCrumbs" :large="!isMobile">
      <template v-slot:divider>
        <v-icon>mdi-chevron-right</v-icon>
      </template>
    </v-breadcrumbs>
    <div v-if="alerts">
      <v-alert
        v-for="(alert, index) of alerts"
        :key="index"
        :value="true"
        :type="alert.type"
        border="left"
        class="my-4 ml-4"
      >
        <v-row align="center">
          <v-col class="grow">
            <p style="white-space: pre-line">
              {{ alert.text }}
            </p>
          </v-col>
          <v-col class="shrink" v-if="alertActions(alert).length">
            <div class="d-flex flex-nowrap">
              <v-btn
                v-for="(alertAction, actionIndex) of alertActions(alert)"
                :key="actionIndex"
                small
                outlined
                class="ml-1"
                :loading="alertAction.loading || alert.loading"
                @click="alertClicked(alertAction)"
              >
                {{ alertAction.text }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-alert>
    </div>

    <div :class="titleContainerHtmlClasses">
      <!-- Left Section -->
      <div :class="titleHtmlClasses">
        <!-- Title Section -->
        <div class="d-flex align-top">
          <div class="flex-grow-1 m-header-title-col">
            <template v-if="$slots.title">
              <div class="mx-4">
                <!-- @slot title -->
                <slot name="title" />
              </div>
            </template>
            <div
              v-else-if="title"
              :class="[
                'font-weight-bold title mx-4 text-wrap d-flex align-start',
                isMobile ? 'mb-0 text-h6' : 'mb-2 text-md-h4'
              ]"
              :style="isMobile ? 'line-height: 1.3' : ''"
            >
              <!-- @slot titlePrepend - content prepended to the left of the title -->
              <slot name="titlePrepend" />
              <span :class="[isMobile ? 'mt-5' : 'mt-3']">{{ title }}</span>
              <v-tooltip right v-if="loading">
                <template v-slot:activator="{ on, attrs }">
                  <v-progress-circular size="14" width="2" indeterminate v-bind="attrs" v-on="on"></v-progress-circular>
                </template>
                <span class="text-small">Loading...</span>
              </v-tooltip>
            </div>
          </div>
          <!-- Action Section -->
          <div :class="actionsContainerHtmlClasses" class="d-print-none mb-n2 mt-2">
            <!-- @slot actions -->
            <template v-if="$slots.actions">
              <div>
                <slot name="actions" />
              </div>
            </template>
            <!-- Default layout -->
            <div v-else>
              <!-- Desktop -->
              <div v-if="!isMobile" class="m-header-actions-row">
                <v-menu :close-on-click="true" :offset-y="true" v-if="otherActions">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" outlined small color="info" class="ma-1">
                      {{ $t("designGuide.MDetailForm.moreActions") }}
                      <v-icon>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-card flat>
                    <v-list class="mx-2" v-for="(action, index) of otherActions" :key="index">
                      <tooltip :text="action.tooltip" :disabled="!action.tooltip">
                        <v-list-item dense @click="actionClicked(action)" :disabled="action.disabled">
                          <v-list-item-icon v-if="action.icon">
                            <v-icon :style="colorStyle(action.color)">{{ action.icon }}</v-icon>
                          </v-list-item-icon>
                          <v-list-item-subtitle :style="colorStyle(action.color)">
                            {{ action.text }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </tooltip>
                    </v-list>
                  </v-card>
                </v-menu>
                <tooltip :text="secondAction.tooltip" :disabled="!secondAction.tooltip" v-if="secondAction">
                  <v-btn
                    outlined
                    small
                    :color="secondAction.color || 'info'"
                    class="ma-1"
                    @click="actionClicked(secondAction)"
                    :disabled="secondAction.disabled"
                    :loading="secondAction.loading"
                  >
                    <v-icon left v-if="secondAction.icon"> {{ secondAction.icon }} </v-icon>
                    {{ secondAction.text }}
                  </v-btn>
                </tooltip>

                <tooltip :text="firstAction.tooltip" :disabled="!firstAction.tooltip" v-if="firstAction">
                  <v-btn
                    elevation="0"
                    small
                    color="info"
                    class="ma-1"
                    :disabled="firstAction.disabled"
                    :loading="firstAction.loading"
                    @click="actionClicked(firstAction)"
                  >
                    <v-icon left v-if="firstAction.icon"> {{ firstAction.icon }} </v-icon>
                    {{ firstAction.text }}
                  </v-btn>
                </tooltip>
              </div>
              <!-- Mobile -->
              <div v-else>
                <v-menu :close-on-click="true" :offset-y="true" v-if="mobileActions">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-card flat>
                    <v-list class="mx-2" v-for="(action, index) of actions" :key="index">
                      <v-list-item dense @click="actionClicked(action)" :disabled="action.disabled">
                        <v-list-item-icon v-if="action.icon">
                          <v-icon :style="colorStyle(action.color)">{{ action.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-subtitle :style="colorStyle(action.color)">
                          {{ action.text }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>
            </div>
          </div>
        </div>

        <!-- Subtitle Section -->
        <template v-if="$slots.subtitle">
          <div class="mx-4">
            <!-- @slot Subtitle -->
            <slot name="subtitle" />
          </div>
        </template>
        <div v-else class="text-medium-emphasis mx-4">
          <span>
            {{ subtitle }}
          </span>
          <span v-for="(chip, index) in chips" :key="index">
            <tooltip :text="chip.tooltip" v-if="chip.tooltip">
              <v-chip
                v-if="!chip.disabled"
                :color="chipColor(chip, index)"
                @click="actionClicked(chip)"
                dark
                :small="!isMobile"
                :x-small="isMobile"
                :class="isMobile ? 'mr-1 text-truncate' : 'mr-2 text-truncate'"
              >
                <v-icon :small="!isMobile" :x-small="isMobile" left v-if="chip.icon">{{ chip.icon }}</v-icon>
                {{ chip.text }}
              </v-chip>
              <v-chip
                v-else
                :color="chipColor(chip, index)"
                dark
                :small="!isMobile"
                :x-small="isMobile"
                :class="isMobile ? 'mr-1 text-truncate' : 'mr-2 text-truncate'"
              >
                <v-icon :small="!isMobile" :x-small="isMobile" left v-if="chip.icon">{{ chip.icon }}</v-icon>
                {{ chip.text }}
              </v-chip>
            </tooltip>
            <v-chip
              v-else-if="!chip.disabled"
              :color="chipColor(chip, index)"
              @click="actionClicked(chip)"
              dark
              :small="!isMobile"
              :x-small="isMobile"
              :class="isMobile ? 'mr-1' : 'mr-2'"
            >
              <v-icon :small="!isMobile" :x-small="isMobile" left v-if="chip.icon">{{ chip.icon }}</v-icon>
              {{ chip.text }}
            </v-chip>
            <v-chip
              v-else
              :color="chipColor(chip, index)"
              dark
              :small="!isMobile"
              :x-small="isMobile"
              :class="isMobile ? 'mr-1' : 'mr-2'"
            >
              <v-icon :small="!isMobile" :x-small="isMobile" left v-if="chip.icon">{{ chip.icon }}</v-icon>
              {{ chip.text }}
            </v-chip>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ConfigModule } from "@/store/modules/config";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { IBreadcrumb } from "@/lib/interfaces/utility/breadcrumb-interface";
import Tooltip from "../tooltip.vue";

export interface IChips extends IAction {
  /**
   * A tooltip displayed
   */
  tooltip?: string;
}

/**
 * Action configuration which is displayed as buttons in the `MHeader` or `MActionList`
 */
export interface IAction {
  /**
   * Is the string to be rendered on the button
   */
  text: string;

  /**
   * The description of the action
   */
  description?: string;

  /**
   * Key to identify that an action is clicked, needs to be implemented via `processAction`
   */
  key: string;

  /**
   * Disables the action
   */
  disabled?: boolean;

  /**
   * Overwrites the button color, defaults to `info`
   */
  color?: string;

  /**
   * Displays an icon, is displayed per default on mobile and in `MActionList`
   */
  icon?: string;

  /**
   * Amount of items
   */
  count?: number;

  /**
   * A function to execute the action
   */
  exec?: (...args: any[]) => any;

  multiple?: IAction[];

  loading?: boolean;

  /**
   * Tooltip text shown on hover
   */
  tooltip?: string;
}

export interface IAlert {
  text: string;
  type: "error" | "warning" | "info";
  action?: IAction;

  /**
   * Additional actions displayed as buttons next to the alert text, after `action`
   */
  actions?: IAction[];

  loading?: boolean;
}

export interface IProperty extends IAction {
  value: string | number | boolean;
}

@Component({
  components: { Tooltip }
})
export default class MHeader extends Vue {
  @Prop()
  breadCrumbs!: IBreadcrumb[];

  @Prop()
  title!: string;

  @Prop()
  subtitle!: string;

  @Prop()
  alerts!: IAlert[];

  @Prop()
  chips!: IChips[];

  @Prop()
  actions!: IAction[];

  @Prop({ default: false })
  loading!: false;

  @Prop({ default: "mb-6" })
  c!: string;

  isMobile = this.$vuetify.breakpoint.smAndDown;

  measureIsMobile() {
    const container = this.$refs.mHeaderContainer as Element;

    const containerWidth = container?.clientWidth || 0;
    // this.$log.debug("measureIsMobile", containerWidth);

    // Breakpoint between xs and sm feels best on detail side of list view. e.g. report detail.
    const breakpoint =
      this.$vuetify.breakpoint.thresholds.xs +
      (this.$vuetify.breakpoint.thresholds.sm - this.$vuetify.breakpoint.thresholds.xs) / 2;

    if (!containerWidth) this.isMobile = this.$vuetify.breakpoint.smAndDown;
    else this.isMobile = containerWidth <= breakpoint;
  }

  get firstAction() {
    if (this.actions && this.actions.length) {
      return this.actions[0];
    }
    return undefined;
  }

  get secondAction() {
    if (this.actions && this.actions.length > 1) {
      return this.actions[1];
    }
    return undefined;
  }

  chipColor(chip: IAction, idx: number) {
    if (!chip.color) {
      return ConfigModule.color.palette[idx];
    } else {
      return chip.color;
    }
  }

  get otherActions() {
    if (this.actions && this.actions.length > 2) {
      return this.actions.slice(2);
    }
    return undefined;
  }

  get mobileActions() {
    if (this.actions && this.actions.length) {
      return this.actions;
    }
    return undefined;
  }

  get shouldActionsBeOnTop() {
    /** Don't alter styling if the slot is used */
    if (this.$slots.title) {
      return false;
    }

    /**
     * Mobile actions always be on the right
     * Slot for actions should not be affected
     * */
    if (this.isMobile || this.$slots.actions) {
      return false;
    }

    /**
     * Move the actions on top if the title if title is long enough(100 chars)
     * and there are more than 1 actions.
     */
    if (this.secondAction && this.title?.length > 100) {
      return true;
    }

    return false;
  }

  get titleContainerHtmlClasses() {
    return this.shouldActionsBeOnTop ? ["title-container", "flex-column"] : ["title-container"];
  }

  get titleHtmlClasses() {
    return ["title-wrapper"];
  }

  get actionsContainerHtmlClasses() {
    return this.shouldActionsBeOnTop ? [] : ["actions-container"];
  }

  /**
   * Returns all actions of an alert, combining the single `action` with the `actions` list
   */
  alertActions(alert: IAlert): IAction[] {
    return [...(alert.action ? [alert.action] : []), ...(alert.actions ?? [])];
  }

  alertClicked(action: IAction | undefined) {
    if (!action) {
      return;
    }

    if (action.exec) {
      action.exec();
      return;
    }

    this.$emit("alertClicked", action);
  }

  chipClicked(action: IAction | undefined) {
    if (action) {
      this.$emit("chipClicked", action);
    }
  }

  /**
   *
   * @param action emits action if action is clicked
   */
  actionClicked(action?: IAction) {
    if (!action) {
      return;
    }

    if (action.exec) {
      action.exec();
      return;
    }

    this.$emit("actionClicked", action);
  }

  @Watch("isMobile")
  onIsMobileChanged(val: boolean) {
    this.$emit("isMobile", val);
  }

  colorStyle(color: string | undefined) {
    if (color) {
      return `color: ${color}`;
    }
  }
}
</script>

<style scoped>
.title-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.title-wrapper {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.m-header-title-col {
  min-width: 0;
}

.m-header-actions-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
}

.actions-container {
  white-space: nowrap;
  vertical-align: top;
  align-items: flex-start;
  flex-shrink: 0;
}
</style>
